import {
    Box,
    Collapse,
    Fab,
    Grid,
    Popper,
    TextField,
    InputAdornment,
    Paper,
    useMediaQuery,
} from "@mui/material";
import { useEffect, useState, useRef, useCallback } from "react";
import { PAGE_TYPE } from "../../../types/pages.type";
import { useDispatch } from "react-redux";
import { setIsScrollHightLight } from "../../../store/reducers/common.slice";
import FilterCardList from "./FilterCardList";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import KeyboardArrowUpOutlinedIcon from '@mui/icons-material/KeyboardArrowUpOutlined';

const AddCardDrawer = ({
    listData,
    handleAddCustomCard,
    selectedCards,
    sx,
    page,
    height,
    scrollHeight,
    scrollWidth,
    onStateUpdate,
    position,
    scrollTop,
    transferType,
    filter,
    isLayoutMode,
    pinTopInLayoutMode = false,
    /** Expand collapsed category / wait for layout so in-category cards can scroll into view */
    prepareScrollToCard,
}) => {    
    const [checked, setChecked] = useState(false);
    const [isOpenCloseAll, setIsOpenCloseAll] = useState(false);
    const [searchText, setSearchText] = useState("");
    const dispatch = useDispatch();
    const isMobile = useMediaQuery('(max-width: 768px)');

    // Mobile FAB state
    const [anchorEl, setAnchorEl] = useState(null);
    const fabButtonRef = useRef(null);
    const drawerRef = useRef(null);

    const handleClick = (event) => {
        anchorEl ? handleClose() : setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setChecked(false);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'custom-filter-menu-popover' : undefined;

    useEffect(() => {
        if (isLayoutMode) {
            setChecked(false);
        }
    }, [isLayoutMode]);

    useEffect(() => {
        onStateUpdate && onStateUpdate(checked);
    }, [checked, onStateUpdate]);

    useEffect(() => {
        // Automatically expand the list when user types in search bar
        if (searchText && searchText.trim() !== "") {
            setChecked(true);
        }
    }, [searchText]);

    useEffect(() => {
        // Mobile: Close FAB popover when checked and scrolling
        if (isMobile && checked && scrollTop > 100) {
            setAnchorEl(null);
        }
    }, [checked, scrollTop, isMobile]);

    useEffect(() => {
        // Close slider when clicking outside
        const handleClickOutside = (event) => {
            if (
                checked &&
                !isLayoutMode &&
                drawerRef.current &&
                !drawerRef.current.contains(event.target) &&
                // Don't close if clicking on FAB button (if it exists)
                (!fabButtonRef.current || !fabButtonRef.current.contains(event.target)) &&
                // Don't close if there's search text (user might be searching)
                !searchText.trim()
            ) {
                setChecked(false);
            }
        };

        if (checked) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [checked, isLayoutMode, searchText]);

    const scrollToSection = useCallback(
        async (targetId) => {
            const id = `${targetId}_scroll`;
            if (prepareScrollToCard) {
                try {
                    await prepareScrollToCard(targetId);
                } catch {
                    /* non-fatal */
                }
            }

            const tryScroll = () => {
                const element = document.getElementById(id);
                if (element) {
                    element.scrollIntoView({
                        behavior: "auto",
                        block: "center",
                    });
                    return true;
                }
                return false;
            };

            if (!tryScroll()) {
                await new Promise((resolve) => {
                    let retries = 0;
                    const maxRetries = 40;
                    const tick = () => {
                        if (tryScroll() || retries >= maxRetries) {
                            resolve();
                            return;
                        }
                        retries += 1;
                        requestAnimationFrame(tick);
                    };
                    requestAnimationFrame(() => {
                        requestAnimationFrame(tick);
                    });
                });
            }

            setTimeout(() => {
                dispatch(setIsScrollHightLight(id));
                setTimeout(() => {
                    dispatch(setIsScrollHightLight(null));
                }, 3800);
            }, 200);
        },
        [prepareScrollToCard, dispatch]
    );

    return (
        <>
            {/* FAB with Popper: mobile only for non-OVERALL pages */}
            {(isMobile && page !== PAGE_TYPE.OVERALL
                && scrollTop > 200
                && !isLayoutMode) && (
                    <div>
                        <Fab
                            sx={{
                                position: "fixed",
                                top: { sm: "15%", xs: "30%", md: "21%", lg: "20%", xl: "20%" },
                                left: "calc(var(--sidebar-width) + 25px)",
                                webkitAnimation: "fadeIn 5s",
                                animation: "fadeIn 5s",
                                color: open ? "#fff" : "#103996",
                                background: open ? "#103996" : "#fff",
                                boxShadow: "0px 5px 8px 0px rgba(0, 0, 0, 0.4)",
                                ":hover": {
                                    background: open ? "#103996" : "#fff",
                                    boxShadow: "0px 0px 14px rgba(72, 121, 245, 0.3)"
                                }
                            }}
                            size="small"
                            color="primary"
                            aria-label="add"
                            onClick={handleClick}
                            ref={fabButtonRef}
                        >
                            {open ? (
                                <KeyboardArrowUpOutlinedIcon sx={{ color: open ? "#fff" : "#103996" }} />
                            ) : (
                                <KeyboardArrowDownOutlinedIcon sx={{ color: open ? "#fff" : "#103996" }} />
                            )}
                        </Fab>
                        <Popper
                            id={id}
                            open={open}
                            anchorEl={anchorEl}
                            onClose={handleClose}
                            placement="bottom-start"
                            modifiers={[
                                {
                                    name: 'flip',
                                    enabled: false,
                                    options: {
                                        altBoundary: false,
                                        rootBoundary: 'document',
                                        padding: 8,
                                    },
                                },
                                {
                                    name: 'preventOverflow',
                                    enabled: false,
                                    options: {
                                        altAxis: false,
                                        altBoundary: false,
                                        tether: false,
                                        rootBoundary: 'document',
                                        padding: 8,
                                    },
                                },
                                {
                                    name: 'arrow',
                                    enabled: true,
                                },
                                {
                                    name: "offset",
                                    options: {
                                        offset: [0, 18],
                                    },
                                },
                            ]}
                            transition
                            keepMounted
                        >
                            {({ TransitionProps }) => (
                                <Collapse
                                    {...TransitionProps}
                                    timeout={1000}
                                >
                                    <Box sx={{
                                        p: 0,
                                        bgcolor: 'background.paper',
                                        border: '1px solid #E6E8ED',
                                        borderRadius: 1,
                                        zIndex: 100000
                                    }}
                                    >
                                        <Box
                                            sx={{
                                                position: "relative",
                                                mt: "3px",
                                                "&::before": {
                                                    backgroundColor: "#fff",
                                                    content: '""',
                                                    display: "block",
                                                    position: "absolute",
                                                    width: 20,
                                                    height: 20,
                                                    top: "-1px",
                                                    left: 15,
                                                    transform: 'translate(-50%, -50%) rotate(45deg)',
                                                    border: '1px solid #E6E8ED',
                                                }
                                            }}
                                        />
                                        <FilterCardList
                                            setSearchText={setSearchText}
                                            searchText={searchText}
                                            isOpenCloseAll={isOpenCloseAll}
                                            handleAddCustomCard={handleAddCustomCard}
                                            setIsOpenCloseAll={setIsOpenCloseAll}
                                            scrollHeight={scrollHeight}
                                            scrollWidth={scrollWidth}
                                            listData={listData}
                                            selectedCards={selectedCards}
                                            scrollToSection={scrollToSection}
                                            type="scroll"
                                            pageProp={page}
                                            transferType={transferType}
                                            filter={filter}
                                        />
                                    </Box>
                                </Collapse>
                            )}
                        </Popper>
                    </div>
                )}
            {/* Desktop: New behavior - Box transforms to fixed position */}
            <Box
                ref={drawerRef}
                {...(position && { position })}
                sx={{
                    ...(page !== PAGE_TYPE.OVERALL && {
                        // Use consistent width calculation that works for both absolute and fixed - responsive
                        width: {
                            xs: "26%",
                            sm: "26%",
                            md: "26%",
                            lg: "28%",
                            xl: "30%"
                        },
                        '@media (max-width:1080px)': {
                            width: '33.5%'
                        },
                        '@media (min-width:1920px)': {
                            width: '30%'
                        },
                        minWidth: '200px',
                        maxWidth: {
                            xs: '400px',
                            sm: '400px',
                            md: '450px',
                            lg: '480px',
                            xl: '500px'
                        },
                        // Smooth transition only for top position - left/marginLeft change instantly to avoid jump
                        transition: 'top 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        // Hide on mobile when scrolling (mobile uses FAB button instead)
                        display: (isMobile && scrollTop > 200) ? 'none' : 'block',
                        // Desktop: Always fixed position like FAB button
                        ...(!isMobile && (!isLayoutMode || pinTopInLayoutMode) ? {
                            position: "fixed",
                            top: isLayoutMode ? "30px" : "165px",
                            left: "calc(var(--sidebar-width) + 25px)",
                            marginLeft: 0,
                            zIndex: 1000,
                            opacity: 1,
                        } : {
                            // Mobile: Absolute position
                            position: "absolute",
                            top: "180px", // Increased for mobile absolute position too
                            left: 0,
                            marginLeft: "20px",
                            zIndex: 99,
                            opacity: 1,
                        })
                    }),
                    // OVERALL page: Restore ORIGINAL logic completely
                    ...(page === PAGE_TYPE.OVERALL && !isMobile && !isLayoutMode && scrollTop > 60 && {
                        position: "fixed",
                        top: { sm: "15%", xs: "30%", md: "18%", lg: "18%", xl: "18%" }, // Reverted to original
                        left: "calc(var(--sidebar-width) + 15px)",
                        marginLeft: 0,
                        zIndex: 1000,
                        opacity: 1,
                        transition: "transform 0.6s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.4s ease",
                        width: {
                            sm: "26%",
                            md: "26%",
                            lg: "28%",
                            xl: "30%"
                        },
                        '@media (max-width:1080px)': {
                            width: '22%'
                        },
                        '@media (min-width:1920px)': {
                            width: '30%'
                        },
                        minWidth: '200px',
                        maxWidth: {
                            sm: '360px',
                            md: '380px',
                            lg: '400px',
                            xl: '450px'
                        },
                    }),
                    ...sx,
                }}
                className={page === PAGE_TYPE.OVERALL ? "overall-additional-box" : ""}
            >
                <Grid
                    container
                    direction="row"
                    justifyContent="center"
                    alignItems="center"
                    spacing={page !== PAGE_TYPE.OVERALL ? 1 : 0}
                >
                    <Grid xs={12}>
                        <Grid
                            container
                            direction={"row"}
                            justifyContent={"center"}
                            alignContent={"center"}
                            alignItems={"center"}
                        >
                            <Grid item xs={12}>
                                <Paper
                                    sx={{
                                        display: 'flex',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        alignItems: { xs: 'stretch', sm: 'center' },
                                        borderRadius: page === PAGE_TYPE.OVERALL ? "3px" : "6px",
                                        boxShadow: 1,
                                        overflow: 'visible',
                                        border: page === PAGE_TYPE.OVERALL
                                            ? '1px solid #e8eaf0'
                                            : '1px solid #e0e0e0',
                                        bgcolor: 'background.paper',
                                        p: page === PAGE_TYPE.OVERALL
                                            ? { xs: 0.2, sm: 0.25 }
                                            : { xs: 0.25, sm: 0.3 },
                                        gap: page === PAGE_TYPE.OVERALL
                                            ? { xs: 0.2, sm: 0.15 }
                                            : { xs: 0.2, sm: 0.2 },
                                        mt: page === PAGE_TYPE.OVERALL
                                            ? { xs: 0.375, sm: 0.375, md: 0.5, lg: 0.375, xl: -0.5 }
                                            : 0.5,
                                        transition: 'all 0.3s ease',
                                        '@media (max-width:1366px)': page === PAGE_TYPE.OVERALL ? {
                                            mt: { xs: 0.375, sm: 0.375, md: 0.5, lg: 1.8, xl: -0.5 }
                                        } : {},
                                        '@media (min-width:1600px) and (max-width:1600px)': page === PAGE_TYPE.OVERALL ? {
                                            mt: -5,
                                        } : {},
                                        '@media (min-width:1620px) and (max-width:1900px)': page === PAGE_TYPE.OVERALL ? {
                                            mt: -2.0,
                                        } : {},
                                    }}
                                >
                                    <Box
                                        sx={{
                                            flex: { xs: '0 0 100%', sm: '0 0 58%' },
                                            width: { xs: '100%', sm: '58%' },
                                            maxWidth: { xs: '100%', sm: '58%' },
                                            cursor: isLayoutMode ? 'default' : 'pointer',
                                            transition: 'all 0.2s ease',
                                            overflow: 'visible',
                                            '&:hover': {
                                                opacity: isLayoutMode ? 1 : 0.8,
                                            },
                                        }}
                                        onClick={() => !isLayoutMode && setChecked(!checked)}
                                    >
                                        <Box
                                            className={`customFilterBoxContent ${checked ? "selected " : null} ${isLayoutMode ? "layout-mode-disabled" : ""} ${page === PAGE_TYPE.OVERALL ? 'customFilterBoxContent-overall' : ''}`}
                                            sx={{
                                                margin: 0,
                                                borderRadius: page === PAGE_TYPE.OVERALL ? '3px' : '3px',
                                                padding: page === PAGE_TYPE.OVERALL
                                                    ? { xs: '3px 10px', sm: '3px 10px', md: '4px 14px' }
                                                    : { xs: '3px 8px', sm: '3px 10px', md: '4px 12px' },
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: '100%',
                                                minHeight: page === PAGE_TYPE.OVERALL
                                                    ? { xs: '26px', sm: '30px' }
                                                    : { xs: '26px', sm: '30px' },
                                                boxShadow: checked
                                                    ? '0px 0px 4px rgba(72, 121, 245, 0.2), 0px 0px 2px rgba(72, 121, 245, 0.15)'
                                                    : page === PAGE_TYPE.OVERALL
                                                        ? '0px 0px 2px rgba(0, 0, 0, 0.08), 0px 0px 1px rgba(0, 0, 0, 0.05)'
                                                        : '0px 0px 3px rgba(0, 0, 0, 0.1), 0px 0px 1px rgba(0, 0, 0, 0.08)',
                                                border: checked
                                                    ? '1px solid rgba(72, 121, 245, 0.4)'
                                                    : '1px solid rgba(0, 0, 0, 0.08)',
                                                transition: 'all 0.2s ease',
                                                overflow: 'hidden',
                                                position: 'relative',
                                            }}
                                        >
                                            <Box
                                                component="span"
                                                className={`customFilterBoxHeader ${page === PAGE_TYPE.OVERALL ? 'customFilterBoxHeader-overall' : ''}`}
                                                sx={{
                                                    fontSize: page === PAGE_TYPE.OVERALL
                                                        ? undefined
                                                        : { xs: '10px', sm: '11px', md: '12.5px' },
                                                    fontWeight: page === PAGE_TYPE.OVERALL ? undefined : 600,
                                                    textAlign: 'center',
                                                    whiteSpace: 'nowrap',
                                                    letterSpacing: page === PAGE_TYPE.OVERALL ? undefined : '0px',
                                                    lineHeight: page === PAGE_TYPE.OVERALL ? undefined : '1.2',
                                                }}
                                            >
                                                View All Data Tabs
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Box
                                        sx={{
                                            flex: { xs: '0 0 100%', sm: '0 0 42%' },
                                            width: { xs: '100%', sm: '42%' },
                                            maxWidth: { xs: '100%', sm: '42%' },
                                            minWidth: { xs: 0, sm: 0 },
                                        }}
                                    >
                                        <TextField
                                            fullWidth
                                            variant="outlined"
                                            placeholder="Search tab"
                                            size="small"
                                            onChange={(e) => setSearchText(e.target.value)}
                                            value={searchText}
                                            onClick={(e) => e.stopPropagation()}
                                            onFocus={(e) => e.stopPropagation()}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: 'background.paper',
                                                    ...(page === PAGE_TYPE.OVERALL ? {
                                                        paddingTop: { xs: '4px', sm: '6px' },
                                                        paddingBottom: { xs: '4px', sm: '6px' },
                                                    } : {
                                                        paddingTop: { xs: '2px', sm: '4px' },
                                                        paddingBottom: { xs: '2px', sm: '4px' },
                                                    }),
                                                    '& fieldset': {
                                                        borderColor: 'transparent',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: 'transparent',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: 'transparent',
                                                    },
                                                },
                                                '& .MuiInputBase-input': {
                                                    py: page === PAGE_TYPE.OVERALL
                                                        ? { xs: 0.2, sm: 0.25 }
                                                        : { xs: 0.2, sm: 0.25 },
                                                    fontSize: page === PAGE_TYPE.OVERALL
                                                        ? { xs: '12px', sm: '12px', md: '13px' }
                                                        : { xs: '11px', sm: '12px', md: '13px' },
                                                },
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment
                                                        position="start"
                                                        sx={page === PAGE_TYPE.OVERALL ? {
                                                            marginLeft: { xs: '4px', sm: '6px' },
                                                            marginRight: { xs: '2px', sm: '4px' },
                                                        } : {
                                                            marginLeft: { xs: '2px', sm: '4px' },
                                                            marginRight: { xs: '1px', sm: '2px' },
                                                        }}
                                                    >
                                                        <SearchIcon sx={{
                                                            color: 'text.secondary',
                                                            fontSize: page === PAGE_TYPE.OVERALL
                                                                ? { xs: '13px', sm: '14px' }
                                                                : { xs: '12px', sm: '14px' }
                                                        }} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid xs={12}>
                        <Collapse orientation="vertical" in={checked} timeout={1000}>
                            <FilterCardList
                                setSearchText={setSearchText}
                                searchText={searchText}
                                isOpenCloseAll={isOpenCloseAll}
                                handleAddCustomCard={handleAddCustomCard}
                                setIsOpenCloseAll={setIsOpenCloseAll}
                                scrollHeight={scrollHeight}
                                scrollWidth={scrollWidth}
                                listData={listData}
                                selectedCards={selectedCards}
                                scrollToSection={scrollToSection}
                                pageProp={page}
                                transferType={transferType}
                                filter={filter}
                                hideSearchBar={true}
                            />
                        </Collapse>
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};

export default AddCardDrawer;